import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Bot } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { api } from "../lib/api";
import { BLOG_COVERS } from "../lib/content";

export default function Blog() {
    const [data, setData] = useState({ blogs: [], categories: [] });
    const [category, setCategory] = useState("All");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const t = setTimeout(() => {
            setLoading(true);
            api.get("/blogs", { params: { category, q: q || undefined } })
                .then((r) => setData(r.data))
                .catch(() => {})
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(t);
    }, [category, q]);

    return (
        <div className="relative overflow-hidden" data-testid="blog-page">
            <div className="glow-orb left-[20%] top-[-5%] h-[340px] w-[480px] bg-blue-600/15" />
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
                <Reveal className="text-center">
                    <Eyebrow>Blog</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="blog-heading">
                        Ideas on <span className="cyan-gradient-text">AI, automation & growth</span>
                    </h1>
                    <p className="mx-auto mt-4 max-w-xl text-slate-400">
                        Practical playbooks for WhatsApp automation, AI calling and lead qualification.
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mx-auto mt-10 flex max-w-2xl flex-col items-center gap-4">
                        <div className="relative w-full">
                            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                            <input
                                data-testid="blog-search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles…"
                                className="w-full rounded-full border border-white/10 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-cyan-500/60"
                            />
                        </div>
                        <div className="flex flex-wrap justify-center gap-2" data-testid="blog-category-filters">
                            {["All", ...data.categories].map((c) => (
                                <button
                                    key={c}
                                    data-testid={`blog-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => setCategory(c)}
                                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
                                        category === c
                                            ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40"
                                            : "border border-white/10 bg-white/5 text-slate-400 hover:text-white"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                </Reveal>

                <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
                    {loading ? (
                        <p className="col-span-full py-16 text-center text-slate-500">Loading articles…</p>
                    ) : data.blogs.length === 0 ? (
                        <p className="col-span-full py-16 text-center text-slate-500" data-testid="blog-empty">No articles found. Try another search.</p>
                    ) : (
                        data.blogs.map((b, i) => (
                            <Reveal key={b.id} delay={(i % 3) * 0.08}>
                                <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block h-full">
                                    <article className="glow-card flex h-full flex-col overflow-hidden rounded-2xl">
                                        <div className={`relative flex h-40 items-center justify-center bg-gradient-to-br ${BLOG_COVERS[b.cover] || BLOG_COVERS.cyan}`}>
                                            <Bot className="h-12 w-12 text-white/30 transition-transform group-hover:scale-110" />
                                            <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-300 backdrop-blur">
                                                {b.category}
                                            </span>
                                        </div>
                                        <div className="flex flex-1 flex-col p-6">
                                            <h2 className="font-display text-lg font-bold leading-snug text-white group-hover:text-cyan-300">
                                                {b.title}
                                            </h2>
                                            <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-400">{b.excerpt}</p>
                                            <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                                                <span>{new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                                                <span className="flex items-center gap-1 font-semibold text-cyan-400">
                                                    Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                                </span>
                                            </div>
                                        </div>
                                    </article>
                                </Link>
                            </Reveal>
                        ))
                    )}
                </div>
            </section>
        </div>
    );
}
